import { z } from 'zod';
/**
 * The creators' assistant: its settings, and what the admin console reads about it
 * (`docs/assistant-plan.md` §3, §11, §12).
 *
 * The settings are one row, `assistant_settings`, written by the admin console and by nothing else
 * (`docs/admin-plan.md` §D5), and read by the API at the start of every turn — so a change is the
 * next question's, with no deploy. The inference key is not here and not in D1: it is a Worker
 * secret (§0.8). Nor are prices: the provider bills every request and says what it billed, and that
 * number is what is recorded and capped (§3, §17.1).
 *
 * The statistics are read from `assistant_turns` and `assistant_tool_calls`, which hold no text —
 * not a question, not an answer, not a search query (§8). Spend counts every account; use leaves
 * the analytics exclusions out (§11.2).
 */
/** How long a conversation is kept, from when it began (§0.7). */
export const ASSISTANT_CONVERSATION_DAYS = 30;
/**
 * How long a question's record is kept: its numbers, its lookups by tool and section, its thumb, and
 * no text (§8, §9.1). Long enough to compare a month with the same month a year before; the Privacy
 * page states it from here.
 */
export const ASSISTANT_RECORD_RETENTION_DAYS = 400;
export const AssistantEffort = z.enum(['low', 'medium', 'high']);
/** A Cheaper Inference model id as its catalogue spells it (`gpt-6-luna`, `claude-haiku-5.5`). */
export const AssistantModelId = z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:/-]*$/, 'A model id is letters, digits and . _ : / -.');
/** One million micro-dollars is a dollar. */
const Micro = (maxDollars) => z.number().int().min(0).max(maxDollars * 1_000_000);
export const AssistantSettings = z.object({
    /** Off: the panel says the assistant is not answering right now, and nothing is sent anywhere. */
    enabled: z.boolean(),
    model: AssistantModelId,
    /** `null`: the model's own default. */
    effort: AssistantEffort.nullable(),
    /** The longest one answer may be, in tokens. */
    maxOutputTokens: z.number().int().min(256).max(32_000),
    /** Lookups one question may make before it must answer. */
    maxToolCalls: z.number().int().min(0).max(20),
    /** Tokens a conversation may hold before its oldest lookups, then turns, are dropped (§9.2). */
    conversationTokenLimit: z.number().int().min(8_000).max(1_000_000),
    /** What one account may spend in a UTC day, in micro-dollars. */
    accountDayMicro: Micro(100),
    /** What everybody together may spend in a calendar month, in micro-dollars. */
    monthMicro: Micro(100_000),
});
/**
 * What the migration seeds: §17.6, the operator's first settings — the model the eval chose, the
 * limits it measured, and switched off until the operator turns it on.
 */
export const ASSISTANT_DEFAULT_SETTINGS = {
    enabled: false,
    model: 'gpt-6-luna',
    effort: null,
    maxOutputTokens: 4_000,
    maxToolCalls: 6,
    conversationTokenLimit: 200_000,
    accountDayMicro: 500_000,
    monthMicro: 50_000_000,
};
/** How a question ended (§11.2). Everything but `answered` is something an operator should see rise. */
export const AssistantOutcome = z.enum([
    'answered',
    'day_limit',
    'month_limit',
    'stopped',
    'lookups_spent',
    'provider_error',
    'error',
]);
export const ASSISTANT_EVALUATIONS = [
    {
        model: 'gpt-6-luna',
        at: '2026-10-21',
        passed: 61,
        graded: 62,
        costPerQuestionMicro: 1_300,
        medianMs: 14_300,
        verdict: 'chosen',
        note: 'AS6: the final pack and the shipping tools, ZDR. Every data and rules question; 84% of input cached.',
    },
    {
        model: 'claude-haiku-5.5',
        at: '2026-10-21',
        passed: 58,
        graded: 62,
        costPerQuestionMicro: 4_700,
        medianMs: 15_000,
        verdict: 'alternative',
        note: 'AS6: nothing cached under ZDR, three invented links, and once declined a price question in words that admit one.',
    },
    {
        model: 'claude-sonnet-5.5',
        at: '2026-10-21',
        passed: 43,
        graded: 47,
        costPerQuestionMicro: 45_400,
        medianMs: 12_300,
        verdict: 'not recommended',
        note: 'AS0: no better than the cheap two at sixty times the price.',
    },
    {
        model: 'deepseek-v4.1-flash',
        at: '2026-10-21',
        passed: 42,
        graded: 47,
        costPerQuestionMicro: 800,
        medianMs: 12_800,
        verdict: 'not recommended',
        note: 'AS0: three answers were its own tool-call markup, unparsed by the gateway.',
    },
    {
        model: 'glm-5.3-flash',
        at: '2026-10-21',
        passed: 38,
        graded: 47,
        costPerQuestionMicro: 600,
        medianMs: 8_100,
        verdict: 'not recommended',
        note: 'AS0: em dashes, invented links, missed facts.',
    },
    {
        model: 'gemini-3.8-flash',
        at: '2026-10-21',
        passed: 16,
        graded: 18,
        costPerQuestionMicro: 0,
        medianMs: 0,
        verdict: 'not recommended',
        note: 'AS0: one request hung for five minutes under ZDR; stopped before it finished.',
    },
];
/* ------------------------------------------------------------------- the dashboard's routes */
/** `POST /v1/assistant/conversations` — pinned to the team of the page it was opened on, or to none. */
export const CreateAssistantConversationRequest = z.object({
    teamId: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'Not a ULID.').nullable().default(null),
});
/** The longest question the panel sends. */
export const ASSISTANT_QUESTION_MAX = 4_000;
/** `POST /v1/assistant/conversations/:id/turns` — a question, and the page the panel was open on. */
export const AssistantTurnRequest = z.object({
    text: z.string().trim().min(1).max(ASSISTANT_QUESTION_MAX),
    /** `location.pathname` and the hash: what "this page" means to the assistant. */
    path: z.string().max(300).regex(/^\//, 'A dashboard path starts with /.').default('/'),
});
/** `PUT /v1/assistant/turns/:turnId/rating` — a thumb, or 0 to take it back. */
export const RateAssistantTurnRequest = z.object({ rating: z.union([z.literal(1), z.literal(-1), z.literal(0)]) });
//# sourceMappingURL=assistant.js.map